import React from 'react';
import { TbCircleCheckFilled, TbClipboardCheck, TbCoinRupee, TbLayoutDashboard } from 'react-icons/tb';

interface DashboardStatsRowProps {
  totalProjects: number;
  billedCount: number;
  paymentReceivedCount: number;
  cancelledCount: number;
  onStatusClick: (status: string, index: number) => void;
  loading?: boolean;
}

const DashboardStatsRow: React.FC<DashboardStatsRowProps> = ({
  totalProjects,
  billedCount,
  paymentReceivedCount,
  cancelledCount,
  onStatusClick,
  loading = false,
}) => {
  // Define the four cards configuration
  const statsCards = [
    {
      key: 'TOTAL',
      label: 'Total Projects',
      icon: <TbLayoutDashboard className='w-full h-full' />,
      bgColor: 'bg-[#fcfdff]',
      borderColor: 'border-[#90b7eb]',
      titleColor: 'text-[#5897ec]',
      valueColor: 'text-[#3279d7]',
      shadowColor: 'shadow-[0px_7px_16px_#307ce226]',
      count: totalProjects,
      index: -1,
    },
    {
      key: 'BILLED',
      label: 'Billed',
      icon: <TbClipboardCheck className='w-full h-full' />,
      bgColor: 'bg-[#fcfffd]',
      borderColor: 'border-[#4ecb6a]',
      titleColor: 'text-[#53c86e]',
      valueColor: 'text-[#28a745]',
      shadowColor: 'shadow-[0px_7px_16px_#28a74526]',
      count: billedCount,
      index: 5,
    },
    {
      key: 'PAYMENT_RECEIVED',
      label: 'Payment Received',
      icon: <TbCircleCheckFilled className='w-full h-full' />,
      bgColor: 'bg-[#f9fffb]',
      borderColor: 'border-[#6ed485]',
      titleColor: 'text-[#6ed485]',
      valueColor: 'text-[#4ebb67]',
      shadowColor: 'shadow-[0px_7px_16px_#6ed48526]',
      count: paymentReceivedCount,
      index: 6,
    },
    {
      key: 'CANCELLED',
      label: 'Cancelled',
      icon: <TbCoinRupee className='w-full h-full' />,
      bgColor: 'bg-[#fffcfc]',
      borderColor: 'border-[#ff1616]',
      titleColor: 'text-[#ff1616]',
      valueColor: 'text-[#ff1616]',
      shadowColor: 'shadow-[0px_7px_16px_#ff161626]',
      count: cancelledCount,
      index: 10,
    },
  ];

  if (loading) {
    return (
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm animate-pulse"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
              <div className="h-12 w-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((card) => (
          <button
            key={card.key}
            onClick={() => onStatusClick(card.key, card.index)}
            className={`
              ${card.bgColor} ${card.borderColor} ${card.shadowColor}
              rounded-2xl border-[0.8px]
              p-6
              transition-all duration-200
              cursor-pointer 
              hover:shadow-xl hover:-translate-y-1
              text-left
            `}
          >
            {/* Header: Icon + Label */}
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <div className={`w-6 h-6 flex-shrink-0 ${card.titleColor}`}>
                {card.icon}
              </div>
              <h3 className={`font-semibold ${card.titleColor} text-lg sm:text-xl tracking-[-0.52px] truncate`}>
                {card.label}
              </h3>
            </div>

            {/* Large Count Number */}
            <p className={`font-extrabold ${card.valueColor} text-4xl tracking-[-0.72px]`}>
              {card.count.toLocaleString()}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DashboardStatsRow;
